import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import RitualHero from "./components/RitualHero";
import RitualPhilosophy from "./components/RitualPhilosophy";
import RitualExperience from "./components/RitualExperience";
import RitualGateway from "./components/RitualGateway";
import RitualMembership from "./components/RitualMembership";
import RitualCTA from "./components/RitualCTA";
import SpiralCanvas from "@/pages/vault/SpiralCanvas";
import CelestialCanvas from "./components/CelestialCanvas";
import AscensionRainCanvas from "./components/AscensionRainCanvas";
import { LoopContext } from "./LoopContext";

const LOOP_COUNT_KEY = "maverick_loop_count";

function getLoopCount(): number {
  const stored = localStorage.getItem(LOOP_COUNT_KEY);
  return stored ? parseInt(stored, 10) : 0;
}

function incrementLoopCount(): number {
  const next = getLoopCount() + 1;
  localStorage.setItem(LOOP_COUNT_KEY, String(next));
  return next;
}

// ── 解錠後の覚醒オーバーレイ（ホームページ到着時） ──
function CovenantArrivalOverlay({ onDone }: { onDone: () => void }) {
  const [phase, setPhase] = useState<"in" | "hold" | "out" | "done">("in");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const progressRef = useRef(0);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("hold"), 800);
    const t2 = setTimeout(() => setPhase("out"), 2200);
    const t3 = setTimeout(() => { setPhase("done"); onDone(); }, 4000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onDone]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;

    // 光の粒子（外から中心へ収束）
    interface Mote {
      x: number; y: number;
      vx: number; vy: number;
      life: number; maxLife: number;
      size: number; opacity: number;
    }
    const motes: Mote[] = Array.from({ length: 60 }, () => {
      const angle = Math.random() * Math.PI * 2;
      const dist = 80 + Math.random() * Math.max(canvas.width, canvas.height) * 0.5;
      return {
        x: cx + Math.cos(angle) * dist,
        y: cy + Math.sin(angle) * dist,
        vx: (cx - (cx + Math.cos(angle) * dist)) * 0.004,
        vy: (cy - (cy + Math.sin(angle) * dist)) * 0.004,
        life: Math.random() * 60,
        maxLife: 100 + Math.random() * 150,
        size: 1 + Math.random() * 2.5,
        opacity: 0.3 + Math.random() * 0.5,
      };
    });

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (phase === "in") {
        progressRef.current = Math.min(progressRef.current + 0.025, 1);
      } else if (phase === "hold") {
        progressRef.current = Math.min(progressRef.current + 0.005, 1);
      } else if (phase === "out") {
        progressRef.current = Math.max(progressRef.current - 0.018, 0);
      }

      const p = progressRef.current;

      // 金白の光のベース
      ctx.fillStyle = `rgba(255, 245, 210, ${p * 0.85})`;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 中央の収束グロー
      const glowR = 60 + (1 - p) * 200;
      const glowGrd = ctx.createRadialGradient(cx, cy, 0, cx, cy, glowR);
      glowGrd.addColorStop(0, `rgba(255, 240, 180, ${p * 0.5})`);
      glowGrd.addColorStop(0.5, `rgba(255, 220, 120, ${p * 0.2})`);
      glowGrd.addColorStop(1, "rgba(255, 200, 80, 0)");
      ctx.fillStyle = glowGrd;
      ctx.beginPath();
      ctx.arc(cx, cy, glowR, 0, Math.PI * 2);
      ctx.fill();

      // 光の粒子
      motes.forEach((m) => {
        m.life++;
        m.x += m.vx * (1 + (1 - p) * 1.5);
        m.y += m.vy * (1 + (1 - p) * 1.5);
        const prog = m.life / m.maxLife;
        const alpha = prog < 0.3
          ? (prog / 0.3) * m.opacity * p
          : (1 - (prog - 0.3) / 0.7) * m.opacity * p;
        if (m.life >= m.maxLife) {
          const angle = Math.random() * Math.PI * 2;
          const dist = 60 + Math.random() * Math.max(canvas.width, canvas.height) * 0.45;
          m.x = cx + Math.cos(angle) * dist;
          m.y = cy + Math.sin(angle) * dist;
          m.vx = (cx - m.x) * 0.004;
          m.vy = (cy - m.y) * 0.004;
          m.life = 0;
        }
        if (alpha <= 0) return;
        ctx.beginPath();
        ctx.arc(m.x, m.y, m.size * p, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 220, 130, ${alpha})`;
        ctx.fill();
      });

      animRef.current = requestAnimationFrame(draw);
    }

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  if (phase === "done") return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ pointerEvents: "none" }}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0"
        style={{ width: "100vw", height: "100vh" }}
      />
      {/* 中央のメッセージ */}
      <div
        className="relative z-10 text-center"
        style={{
          opacity: phase === "hold" ? 1 : 0,
          transition: "opacity 1s ease",
        }}
      >
        <p
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: "rgba(80, 50, 10, 0.7)",
            fontSize: "clamp(1rem, 2.5vw, 1.4rem)",
            fontWeight: 300,
            letterSpacing: "0.15em",
            fontStyle: "italic",
          }}
        >
          神々の許しを得た。
          <br />
          <span style={{ fontSize: "0.75em", color: "rgba(100, 65, 15, 0.55)", letterSpacing: "0.2em" }}>
            炎は光となり、あなたを包む。
          </span>
        </p>
      </div>
    </div>
  );
}

export default function Home() {
  const [spiralActive, setSpiralActive] = useState(false);
  const [currentLoopCount, setCurrentLoopCount] = useState(() => getLoopCount());
  const [showArrival, setShowArrival] = useState(false);
  const navigate = useNavigate();

  const isUnlocked = sessionStorage.getItem("vault_unlocked") === "true";
  const isAscensionLeader = currentLoopCount >= 5;

  useEffect(() => {
    const justArrived = sessionStorage.getItem("vault_just_arrived") === "true";
    if (isUnlocked && justArrived) {
      sessionStorage.removeItem("vault_just_arrived");
      setShowArrival(true);
    }
  }, [isUnlocked]);

  const handleCTAClick = useCallback(() => {
    const count = incrementLoopCount();
    setCurrentLoopCount(count);
    setSpiralActive(true);
  }, []);

  const handleSpiralComplete = useCallback(() => {
    setSpiralActive(false);
    if (!isUnlocked && currentLoopCount >= 5) {
      navigate("/abyss");
    } else {
      navigate("/", { replace: true });
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [navigate, currentLoopCount, isUnlocked]);

  return (
    <LoopContext.Provider value={{ loopCount: currentLoopCount, isUnlocked }}>
      <SpiralCanvas
        active={spiralActive}
        onComplete={handleSpiralComplete}
        loopCount={currentLoopCount}
      />

      {showArrival && (
        <CovenantArrivalOverlay onDone={() => setShowArrival(false)} />
      )}

      {isUnlocked && <CelestialCanvas />}

      <AscensionRainCanvas active={isAscensionLeader} />

      <main
        className="bg-[#080608] min-h-screen"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          opacity: spiralActive ? 0 : 1,
          transition: spiralActive ? "opacity 0.6s ease" : "none",
          pointerEvents: spiralActive ? "none" : "auto",
        }}
      >
        <RitualHero />
        <RitualPhilosophy />
        <RitualExperience />
        <RitualGateway />
        <RitualMembership />
        <RitualCTA onEnter={handleCTAClick} loopCount={currentLoopCount} isUnlocked={isUnlocked} />
      </main>
    </LoopContext.Provider>
  );
}
